// Image upload field — circular avatar style, used for logos and profile photos
// Uses accept/maxFiles from field config instead of hardcoded values
import { useRef, useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatAcceptList, getStoredAssetUrl, isFileAccepted } from '@startus/lib';
import { FieldError, FieldWrapper } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import uploadIcon from '../../assets/icons/upload-icon.svg';
import type { BaseFieldProps } from '../../types/fields';

export function ImageField({ name, label, helpText, accept, maxFiles: _maxFiles }: BaseFieldProps) {
  const { t } = useTranslation('common');
  const { isReadOnly } = useFormMode();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  // Picked file rejected by `accept` — kept out of the form, explained under the field.
  const [typeError, setTypeError] = useState<string | null>(null);
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = errors[name]?.message as string | undefined;

  // Accept string for <input>: [".jpg", ".png"] → ".jpg,.png", fallback to image/*
  const acceptAttr = accept?.join(',') ?? 'image/*';

  // preview being set doesn't guarantee the browser can render it — e.g. an
  // .ai (Adobe Illustrator) upload is a valid file but not a
  // browser-renderable image format, so the <img> load fails silently
  // without this. There's no startup/field name available here to fall
  // back to initials (unlike the page-level avatar components), so a
  // failed load just reverts to the empty preview state.
  const [previewFailed, setPreviewFailed] = useState(false);

  // Generate preview URL from File object or backend string URL
  useEffect(() => {
    setPreviewFailed(false);
    if (!value) {
      setPreview(null);
      return;
    }
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    // BE stores a JSON URL list; legacy non-URL text yields no preview instead of a broken image.
    setPreview(getStoredAssetUrl(value));
  }, [value]);

  if (isReadOnly) {
    return (
      <FieldWrapper>
        <div className="w-32 h-32 rounded-full bg-base-300 overflow-hidden flex items-center justify-center">
          {preview && !previewFailed ? (
            <img
              src={preview}
              alt={label}
              className="w-full h-full object-cover"
              onError={() => setPreviewFailed(true)}
            />
          ) : null}
        </div>
        {helpText && <p className="text-sm text-base-content/60 mt-1">{helpText}</p>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur } }) => (
          <div className="w-full justify-center flex">
            <div className="relative w-32 h-32">
              <div className="w-32 h-32 rounded-full bg-base-300 overflow-hidden">
                {preview && !previewFailed ? (
                  <img
                    src={preview}
                    alt={label}
                    className="w-full h-full object-cover"
                    onError={() => setPreviewFailed(true)}
                  />
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="absolute bottom-1 right-1 btn btn-primary btn-sm px-2"
              >
                <img src={uploadIcon} alt="Upload" className="w-4 h-4" />
              </button>
              <input
                type="file"
                accept={acceptAttr}
                ref={inputRef}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  e.target.value = '';
                  if (file && !isFileAccepted(file, accept)) {
                    setTypeError(
                      t('formFields.invalidFileType', { formats: formatAcceptList(accept ?? []) })
                    );
                    return;
                  }
                  setTypeError(null);
                  onChange(file);
                }}
                onBlur={onBlur}
              />
            </div>
          </div>
        )}
      />
      {helpText && <p className="text-sm text-base-content/60 mt-1">{helpText}</p>}
      {typeError ? (
        <p role="alert" className="label-text-alt text-error py-1">
          {typeError}
        </p>
      ) : (
        <FieldError message={error} />
      )}
    </FieldWrapper>
  );
}
